// useWorkflowAgents — read a Chief of Staff chat's workflow runs and their
// agents' saved conversations from the runner (plan
// design/2026-10-26-chief-of-staff-agents-panel.md, T5).
//
// A workflow reports itself to the chat only as a whole; each of its agents'
// conversations is on disk in the owner's home. Two read-only routes serve
// them:
//
//   GET …/session/:id/workflows/:run                → WorkflowRun
//   GET …/session/:id/workflows/:run/agents/:agent  → WorkflowTranscript (404: no longer saved)
//
// Fetching goes through a `WorkflowAgentsSource` read from context, so the
// design showcase can supply fixture data. Polls are shared per source and
// key: however many components read a run, it is fetched once every 3 s while
// any reader says it is running, once more after the last reader says it
// stopped, and not at all once it has settled.

import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import { DEFAULT_ENDPOINTS, type AgentChatEndpoints } from '../plugins.js'
import type { WorkflowRun, WorkflowTranscript } from '../types.js'

/** How often a running run or agent is re-read. */
export const WORKFLOW_POLL_MS = 3000

/** The fetch seam. Rejects with `{ status }` on an HTTP error. */
export interface WorkflowAgentsSource {
  run(sessionId: string, runId: string, signal: AbortSignal): Promise<WorkflowRun>
  transcript(sessionId: string, runId: string, agentId: string, signal: AbortSignal): Promise<WorkflowTranscript>
}

export const WorkflowAgentsSourceContext = createContext<WorkflowAgentsSource | null>(null)

/** The source AgentChat provides: the chat's endpoints and auth header. */
export function createWorkflowAgentsSource({ apiBaseUrl = '', endpoints, authHeader }: {
  apiBaseUrl?: string
  endpoints?: Partial<AgentChatEndpoints>
  authHeader?: string
}): WorkflowAgentsSource {
  const ep = { ...DEFAULT_ENDPOINTS, ...endpoints }
  const get = async <T,>(path: string, signal: AbortSignal): Promise<T> => {
    const headers: Record<string, string> = {}
    if (authHeader) headers['Authorization'] = authHeader
    const res = await fetch(`${apiBaseUrl}${path}`, { headers, signal })
    if (!res.ok) {
      let message = `HTTP ${res.status}`
      try {
        const body = await res.json() as { error?: unknown }
        if (typeof body?.error === 'string' && body.error) message = body.error
      } catch { /* not JSON */ }
      throw { status: res.status, message }
    }
    return await res.json() as T
  }
  return {
    run: (sessionId, runId, signal) => get<WorkflowRun>(ep.workflowRun(sessionId, runId), signal),
    transcript: (sessionId, runId, agentId, signal) =>
      get<WorkflowTranscript>(ep.workflowTranscript(sessionId, runId, agentId), signal),
  }
}

// ── The shared poll store ──────────────────────────────────────────────────

interface PollState<T> {
  data?: T
  error?: string
  notFound: boolean
}

interface Subscriber {
  running: boolean
  notify: () => void
}

interface Poll<T> {
  state: PollState<T>
  fetcher: (signal: AbortSignal) => Promise<T>
  subscribers: Set<Subscriber>
  timer?: ReturnType<typeof setInterval>
  inFlight?: { controller: AbortController; whileRunning: boolean }
  /** A fetch begun after every reader said "not running" has completed. */
  settled: boolean
}

const stores = new WeakMap<WorkflowAgentsSource, Map<string, Poll<unknown>>>()

function storeFor(source: WorkflowAgentsSource): Map<string, Poll<unknown>> {
  let s = stores.get(source)
  if (!s) { s = new Map(); stores.set(source, s) }
  return s
}

function errorStatus(err: unknown): number | undefined {
  const status = (err as { status?: unknown } | null)?.status
  return typeof status === 'number' ? status : undefined
}

function errorMessage(err: unknown): string {
  const status = errorStatus(err)
  if (status === 503) return 'The agent runner is not answering. Try again in a moment.'
  const message = (err as { message?: unknown } | null)?.message
  if (typeof message === 'string' && message) return message
  return status ? `Could not load this (HTTP ${status}).` : 'Could not load this.'
}

const anyRunning = (p: Poll<unknown>) => [...p.subscribers].some((s) => s.running)

function startFetch(p: Poll<unknown>) {
  p.inFlight?.controller.abort()
  const controller = new AbortController()
  const whileRunning = anyRunning(p)
  p.inFlight = { controller, whileRunning }
  p.fetcher(controller.signal).then(
    (data) => {
      if (controller.signal.aborted) return
      p.inFlight = undefined
      p.state = { data, notFound: false }
      if (!whileRunning) p.settled = true
      p.subscribers.forEach((s) => s.notify())
    },
    (err) => {
      if (controller.signal.aborted) return
      p.inFlight = undefined
      p.state = errorStatus(err) === 404
        ? { data: p.state.data, notFound: true }
        : { data: p.state.data, error: errorMessage(err), notFound: false }
      // A 404 is an answer; a failed final read is not, so Retry may make it.
      if (!whileRunning && errorStatus(err) === 404) p.settled = true
      p.subscribers.forEach((s) => s.notify())
    },
  )
}

/** Bring a poll's timer and requests in line with its readers. */
function reconcile(p: Poll<unknown>) {
  if (p.subscribers.size === 0) {
    if (p.timer) { clearInterval(p.timer); p.timer = undefined }
    p.inFlight?.controller.abort()
    p.inFlight = undefined
    return
  }
  if (anyRunning(p)) {
    p.settled = false
    if (!p.timer) {
      // A tick never aborts a read still going: a slow run or transcript
      // (up to 4 MiB) would otherwise never land while the work runs.
      p.timer = setInterval(() => { if (!p.inFlight) startFetch(p) }, WORKFLOW_POLL_MS)
      if (!p.inFlight) startFetch(p)
    }
    return
  }
  if (p.timer) { clearInterval(p.timer); p.timer = undefined }
  // The one last read after the work stopped: a request begun while it ran
  // may predate the end, so it does not count.
  if (!p.settled && !(p.inFlight && !p.inFlight.whileRunning)) startFetch(p)
}

function subscribe<T>(
  source: WorkflowAgentsSource,
  key: string,
  fetcher: (signal: AbortSignal) => Promise<T>,
  running: boolean,
  notify: () => void,
): () => void {
  const store = storeFor(source)
  let p = store.get(key) as Poll<T> | undefined
  if (!p) {
    p = { state: { notFound: false }, fetcher, subscribers: new Set(), settled: false }
    store.set(key, p as Poll<unknown>)
  }
  const sub: Subscriber = { running, notify }
  p.subscribers.add(sub)
  reconcile(p as Poll<unknown>)
  const poll = p
  return () => {
    poll.subscribers.delete(sub)
    reconcile(poll as Poll<unknown>)
  }
}

function retryPoll(source: WorkflowAgentsSource | null, key: string) {
  if (!source) return
  const p = storeFor(source).get(key)
  if (!p || p.subscribers.size === 0) return
  p.state = { ...p.state, error: undefined }
  p.subscribers.forEach((s) => s.notify())
  startFetch(p)
}

function readPoll<T>(source: WorkflowAgentsSource | null, key: string): PollState<T> | undefined {
  if (!source) return undefined
  return storeFor(source).get(key)?.state as PollState<T> | undefined
}

const runKey = (sessionId: string, runId: string) => `run\u0000${sessionId}\u0000${runId}`
const transcriptKey = (sessionId: string, runId: string, agentId: string) =>
  `transcript\u0000${sessionId}\u0000${runId}\u0000${agentId}`

// ── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Every workflow run of a chat. A run with no `runId` is skipped. The same
 * run named twice is read once; a run is running if any entry says so.
 */
export function useWorkflowRuns(sessionId: string, runs: { runId: string; running: boolean }[]): {
  runs: ReadonlyMap<string, WorkflowRun>
  errors: ReadonlyMap<string, string>
  retry(runId: string): void
} {
  const source = useContext(WorkflowAgentsSourceContext)
  const [version, bump] = useReducer((n: number) => n + 1, 0)

  const wanted = new Map<string, boolean>()
  for (const r of runs) if (r.runId) wanted.set(r.runId, (wanted.get(r.runId) ?? false) || r.running)
  // The trigger: which runs, and whether each is running. Nothing else.
  const key = [...wanted].sort(([a], [b]) => a.localeCompare(b)).map(([id, on]) => `${id}:${on ? 1 : 0}`).join(',')

  useEffect(() => {
    if (!source || !sessionId || !key) return
    const unsubs = key.split(',').map((entry) => {
      const sep = entry.lastIndexOf(':')
      const runId = entry.slice(0, sep)
      return subscribe(source, runKey(sessionId, runId), (signal) => source.run(sessionId, runId, signal), entry.slice(sep + 1) === '1', bump)
    })
    return () => unsubs.forEach((u) => u())
  }, [source, sessionId, key])

  const result = useMemo(() => {
    const out = new Map<string, WorkflowRun>()
    const errors = new Map<string, string>()
    if (key) {
      for (const entry of key.split(',')) {
        const runId = entry.slice(0, entry.lastIndexOf(':'))
        const st = readPoll<WorkflowRun>(source, runKey(sessionId, runId))
        if (st?.data) out.set(runId, st.data)
        if (st?.error) errors.set(runId, st.error)
        else if (st?.notFound) errors.set(runId, 'This workflow is not available.')
      }
    }
    return { runs: out, errors }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version is the store's change signal
  }, [source, sessionId, key, version])

  const retry = useCallback((runId: string) => retryPoll(source, runKey(sessionId, runId)), [source, sessionId])
  return useMemo(() => ({ ...result, retry }), [result, retry])
}

/** One workflow agent's saved conversation; polled while it runs. */
export function useWorkflowTranscript(sessionId: string, runId: string, agentId: string, running: boolean): {
  transcript?: WorkflowTranscript
  error?: string
  notFound: boolean
  retry(): void
} {
  const source = useContext(WorkflowAgentsSourceContext)
  const [, bump] = useReducer((n: number) => n + 1, 0)
  const key = sessionId && runId && agentId ? transcriptKey(sessionId, runId, agentId) : ''

  useEffect(() => {
    if (!source || !key) return
    return subscribe(source, key, (signal) => source.transcript(sessionId, runId, agentId, signal), running, bump)
    // The trigger is the key (which names session, run and agent) and running.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, key, running])

  const st = key ? readPoll<WorkflowTranscript>(source, key) : undefined
  return {
    transcript: st?.data,
    error: st?.error,
    notFound: st?.notFound ?? false,
    retry: () => { if (key) retryPoll(source, key) },
  }
}
